import React, { useState } from "react";
import { Layers, Plus, Wand2 } from "lucide-react";
import { CaseRow, RelabelProductRow, CaseRoutingStatus, RelabelCaseRoutingType } from "src/types";
import { ControlButton } from "src/components";
import { RelabelCaseBuilderMapper } from "src/mappers";
import { CaseCard } from "./caseCard";

interface CaseBuilderSectionProps {
  purchaseOrderId: string;
  relabelProducts: RelabelProductRow[];
  cases: CaseRow[];
  onCasesChange: (cases: CaseRow[]) => void;
  isReadOnly: boolean;
}

function determineCaseRoutingType(
  products: { quantity: string; routingType: RelabelCaseRoutingType }[]
): CaseRoutingStatus {
  const activeProducts = products.filter((p) => Number(p.quantity) > 0);
  if (activeProducts.length === 0) return "EMPTY";

  const types = new Set(activeProducts.map((p) => p.routingType));
  if (types.size > 1) return "MIXED";
  if (activeProducts.length > 1) return "MULTIPLE_PRODUCTS";
  return activeProducts[0].routingType;
}

function makeEmptyProduct(p: RelabelProductRow) {
  return {
    id: p.id,
    skuName: p.skuName,
    name: p.name,
    barcode: p.barcode,
    size: p.size,
    quantity: "0",
    routingType: p.routingType
  };
}

function availableQtyOf(p: RelabelProductRow): number {
  return Math.max(0, p.euReceivedQty - p.existingCasedQty);
}

// One SKU per case. Each chunk becomes one case holding a single product.
//   - numberOfCases < numSKUs: the top N SKUs by quantity each get their own case.
//   - numberOfCases === numSKUs: every SKU gets its own case with its full quantity.
//   - numberOfCases > numSKUs: every SKU gets a case, then each surplus case halves
//     the current highest-quantity chunk (re-scanning after each split so newly
//     created chunks compete on equal footing).
function autoDistribute(relabelProducts: RelabelProductRow[], numberOfCases: number): CaseRow[] {
  if (numberOfCases <= 0) return [];

  const activeProducts = relabelProducts.filter((p) => availableQtyOf(p) > 0);
  if (activeProducts.length === 0) {
    return Array.from({ length: numberOfCases }, (_, i) =>
      RelabelCaseBuilderMapper.createEmptyCase(i + 1, relabelProducts)
    );
  }

  const sorted = [...activeProducts].sort((a, b) => {
    const aAvail = availableQtyOf(a);
    const bAvail = availableQtyOf(b);
    if (bAvail !== aAvail) return bAvail - aAvail;
    return a.id.localeCompare(b.id);
  });

  type Chunk = { productId: string; quantity: number };
  const chunks: Chunk[] = [];

  if (numberOfCases <= sorted.length) {
    for (let i = 0; i < numberOfCases; i++) {
      chunks.push({ productId: sorted[i].id, quantity: availableQtyOf(sorted[i]) });
    }
  } else {
    for (const p of sorted) {
      chunks.push({ productId: p.id, quantity: availableQtyOf(p) });
    }
    let surplus = numberOfCases - sorted.length;
    while (surplus > 0) {
      let bestIdx = -1;
      for (let i = 0; i < chunks.length; i++) {
        if (chunks[i].quantity < 2) continue;
        if (bestIdx === -1 || chunks[i].quantity > chunks[bestIdx].quantity) {
          bestIdx = i;
        }
      }
      if (bestIdx === -1) break;
      const target = chunks[bestIdx];
      const larger = Math.ceil(target.quantity / 2);
      const smaller = target.quantity - larger;
      target.quantity = larger;
      chunks.push({ productId: target.productId, quantity: smaller });
      surplus--;
    }
  }

  const positionById = new Map(relabelProducts.map((p, idx) => [p.id, idx]));
  chunks.sort((a, b) => (positionById.get(a.productId) ?? 0) - (positionById.get(b.productId) ?? 0));

  const cases: CaseRow[] = chunks.map((chunk, i) => {
    const products = relabelProducts.map((p) => ({
      ...makeEmptyProduct(p),
      quantity: p.id === chunk.productId ? String(chunk.quantity) : "0"
    }));
    return { caseNumber: i + 1, routingType: determineCaseRoutingType(products), products };
  });

  while (cases.length < numberOfCases) {
    cases.push(RelabelCaseBuilderMapper.createEmptyCase(cases.length + 1, relabelProducts));
  }

  return cases;
}

const CaseBuilderSection: React.FC<CaseBuilderSectionProps> = ({
  purchaseOrderId,
  relabelProducts,
  cases,
  onCasesChange,
  isReadOnly
}) => {
  const [numberOfCases, setNumberOfCases] = useState<string>(() => {
    const activeProductCount = relabelProducts.filter((p) => availableQtyOf(p) > 0).length;
    return String(Math.max(activeProductCount, 1));
  });

  const handleProductQtyChange = (caseNumber: number, productId: string, quantity: string) => {
    const updated = cases.map((c) => {
      if (c.caseNumber !== caseNumber) return c;
      const updatedProducts = c.products.map((p) => (p.id === productId ? { ...p, quantity } : p));
      return { ...c, products: updatedProducts, routingType: determineCaseRoutingType(updatedProducts) };
    });
    onCasesChange(updated);
  };

  const handleDeleteCase = (caseNumber: number) => {
    const filtered = cases.filter((c) => c.caseNumber !== caseNumber);
    const renumbered = filtered.map((c, idx) => ({ ...c, caseNumber: idx + 1 }));
    onCasesChange(renumbered);
  };

  const handleAddEmptyCase = () => {
    const newCase = RelabelCaseBuilderMapper.createEmptyCase(cases.length + 1, relabelProducts);
    onCasesChange([...cases, newCase]);
  };

  const handleAutoDistribute = () => {
    const n = Number(numberOfCases);
    if (n <= 0) return;
    const distributed = autoDistribute(relabelProducts, n);
    onCasesChange(distributed);
  };

  const availableByProductId: Record<string, number> = {};
  for (const p of relabelProducts) {
    availableByProductId[p.id] = availableQtyOf(p);
  }

  const inProgressByProductId: Record<string, number> = {};
  for (const c of cases) {
    for (const p of c.products) {
      const q = Number(p.quantity) || 0;
      if (q > 0) inProgressByProductId[p.id] = (inProgressByProductId[p.id] ?? 0) + q;
    }
  }

  return (
    <div className="rounded-2xl border border-secondary-200 bg-white shadow-sm overflow-hidden">
      <div className="bg-black px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-white/10 p-2">
            <Layers className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-white tracking-wide">EU Stock to Cartonise</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Create cases for the EU stock from {purchaseOrderId}. Each case gets a barcode label linking to this
              order.
            </p>
          </div>
        </div>
      </div>

      <div className="p-5">
        {!isReadOnly && (
          <div className="flex flex-wrap items-center gap-3 mb-5 pb-5 border-b border-secondary-100">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-secondary-700">Number of cases:</span>
              <input
                type="number"
                min="1"
                max="200"
                value={numberOfCases}
                onChange={(e) => setNumberOfCases(e.target.value)}
                className="w-16 text-center border border-secondary-300 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              />
            </div>
            <ControlButton
              onClick={handleAutoDistribute}
              text={`Auto-distribute into ${numberOfCases} cases`}
              icon={<Wand2 className="h-4 w-4" />}
            />
            <ControlButton onClick={handleAddEmptyCase} text="Add Empty Case" icon={<Plus className="h-4 w-4" />} />
          </div>
        )}

        {cases.length === 0 ? (
          <div className="text-center py-8 text-secondary-400 text-sm">
            No cases created yet. Use auto-distribute or add an empty case to get started.
          </div>
        ) : (
          <div className="space-y-4">
            {cases.map((caseRow) => (
              <CaseCard
                key={caseRow.caseNumber}
                caseRow={caseRow}
                onProductQtyChange={handleProductQtyChange}
                onDeleteCase={handleDeleteCase}
                isReadOnly={isReadOnly}
                availableByProductId={availableByProductId}
                inProgressByProductId={inProgressByProductId}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export { CaseBuilderSection };
